/**
 * **A fila do Painel da Loja e o caminho do pedido dentro dela** — `CONTRACTS §8.2` (grupo
 * "Painel da Loja"), `API §6.19`, `§6.20.11`, `ADR-0013`.
 *
 * As nove linhas de rota que este arquivo tipa:
 * `GET /v1/store/orders` · `POST …/{id}/accept` · `…/reject` · `…/items/{itemId}/pick` ·
 * `…/items/{itemId}/shortage` · `…/ready` · `…/dispatch` · `…/deliver` · `…/print`.
 *
 * ## A decisao que governa a FILA: ela e pequena de proposito
 *
 * `ADR-0013 §3` fixa **polling de 5 s** como o mecanismo do MVP, e `API §6.19` a marca
 * `no-store`, `limit` ate 200. Com N paineis abertos, cada campo desta resposta e multiplicado
 * por `N x 12 req/min`. Entao a linha da fila carrega **so o que decide um olhar**: ha quanto
 * tempo o pedido espera, quanto falta do SLA, modalidade, se ja foi pago, quantos itens (e
 * quantos ja separados). O que **nao** carrega:
 *
 * | Fora da linha da fila | Por que |
 * | --- | --- |
 * | nome do item | `Order.items` e `SENSITIVE_HEALTH` (`ADR-0015`), e uma fila de 40 linhas com nome de medicamento e inferencia de saude em massa (REG-044, RN-123) — o mesmo motivo pelo qual `OrderSummarySchema` do cliente so tem `itemCount` |
 * | nome e telefone do cliente | a matriz de `SECURITY §4.2` da ao operador `R minimo (nome, telefone do pedido)`; "do pedido" e o pedido **aberto**, nao a fila inteira. Os dois aparecem no detalhe e no ticket, que e onde o balconista precisa deles |
 * | endereco | idem: e do ticket e do detalhe, nao da triagem |
 *
 * ## `sinceSeq` — o delta, e por que ele e uma sequencia e nao um `updatedAt`
 *
 * `API §6.19` e `ADR-0013 §3`: o parametro le `outbox_event.storeSeq`, alocado de
 * `store.panelSeq` **na mesma transacao** do evento. Um `?since=<timestamp>` perderia o evento
 * cuja transacao comitou fora de ordem de relogio; a sequencia e alocada sob lock de linha da
 * filial, entao a ordem do `seq` **e** a ordem de commit. E a mesma sequencia que o `id:` do
 * SSE previsto usara — nao ha uma segunda numeracao (X-30).
 */
import { z } from 'zod';

import type { AcceptanceSlaStep } from '@farmacia/domain';

import {
  CrfSchema,
  InstantSchema,
  MoneySchema,
  NonNegativeMoneySchema,
  OptimisticVersionSchema,
  OrderCodeSchema,
  PAGE_LIMIT_MAX_STORE_ORDERS,
  Uuidv7Schema,
} from '../common';
import {
  FulfillmentModeSchema,
  OrderChannelSchema,
  OrderStatusSchema,
  PaymentMethodSchema,
  PaymentStatusSchema,
} from '../enums';

// ---------------------------------------------------------------------------
// Cache
// ---------------------------------------------------------------------------

/**
 * `API §6.19`: a fila e **`no-store`**.
 *
 * `ADR-0013 §1` diz por que, e a frase e categorica: o painel *"nao pode servir fila de
 * cache"*. Uma fila guardada por 5 s numa borda e um pedido que o balconista nao ve — e o
 * modo de falha que o ADR inteiro existe para evitar (R11).
 */
export const STORE_QUEUE_CACHE_CONTROL = 'private, no-store';

/** Toda rota do Painel responde `no-store`: transicao de pedido nao se guarda. */
export const STORE_ORDER_CACHE_CONTROL = 'private, no-store';

// ---------------------------------------------------------------------------
// A fila
// ---------------------------------------------------------------------------

/**
 * As tres colunas de `ADR-0013 §2`, P1: **Novos / Em separacao / Prontos**.
 *
 * O filtro e por coluna, e nao por `OrderStatus` cru, porque a coluna e o conceito da tela e
 * o mapa coluna -> estados e do produto. `PRONTOS` inclui `EM_ROTA`: um pedido despachado
 * continua aberto para o balcao ate a entrega ser confirmada, e some da fila so quando chega
 * a um terminal.
 */
export const STORE_QUEUE_COLUMN_VALUES = ['NOVOS', 'EM_SEPARACAO', 'PRONTOS'] as const;
export const StoreQueueColumnSchema = z.enum(STORE_QUEUE_COLUMN_VALUES);
export type StoreQueueColumn = z.infer<typeof StoreQueueColumnSchema>;

/**
 * O degrau de SLA da linha — `ADR-0013 §2` P6 e a revisao ADR-0019 §2: 5/10/20 min sao
 * **default de perfil** (`counter.sla.{highlight,escalate,autoCancel}Seconds`), nao numero
 * do produto. O enum e do produto; os segundos vem do perfil.
 *
 * O `satisfies` amarra esta lista a `AcceptanceSlaStep` de `@farmacia/domain` — a escada que o
 * Painel e o job `order-sla` compartilham. Sem ele, acrescentar um degrau no dominio (ou
 * renomear um aqui) compilaria dos dois lados e produziria um `slaLevel` que a tela nao sabe
 * pintar. Mesma disciplina de `enums.ts`: *"eles nao sao redigitados aqui"*.
 */
export const STORE_QUEUE_SLA_LEVEL_VALUES = [
  'NO_PRAZO',
  'DESTAQUE',
  'ESCALADO',
  'ESTOURADO',
] as const satisfies readonly AcceptanceSlaStep[];
export const StoreQueueSlaLevelSchema = z.enum(STORE_QUEUE_SLA_LEVEL_VALUES);
export type StoreQueueSlaLevel = z.infer<typeof StoreQueueSlaLevelSchema>;

/**
 * `GET /v1/store/orders?column=&sinceSeq=&limit=`.
 *
 * **Nao ha `storeId`.** `API §1.2` recusa `X-Store-Id` e `SECURITY §4.4` explica por que: o
 * alcance vem do vinculo (`staff_store_membership`) e do turno aberto, nunca do que a
 * requisicao pediu. Um parametro de filial aqui seria o IDOR chegando pela porta da frente —
 * e, com `.strict()`, manda-lo responde `422`.
 */
export const StoreQueueQuerySchema = z
  .object({
    column: StoreQueueColumnSchema.optional(),
    /**
     * Delta desde a ultima sequencia vista (`API §6.19`, `ADR-0013 §3`). Ausente = a fila
     * inteira. `0` e valido e significa "desde o inicio" — e por isso o campo e
     * `nonnegative`, e nao `positive`.
     */
    sinceSeq: z.coerce.number().int().nonnegative().optional(),
    limit: z.coerce.number().int().min(1).max(PAGE_LIMIT_MAX_STORE_ORDERS).default(50),
  })
  .strict();
export type StoreQueueQuery = z.infer<typeof StoreQueueQuerySchema>;

/**
 * Uma linha da fila. Ver o cabecalho para o que ficou de fora e por que.
 *
 * `waitingSeconds` e **derivado do servidor**, e nao calculado na tela a partir de
 * `createdAt`: o tablet do balcao tem relogio proprio e ele erra. O mesmo vale para
 * `slaRemainingSeconds` — o SLA e da instancia, e a tela nao conhece o perfil.
 */
export const StoreQueueItemSchema = z.object({
  id: Uuidv7Schema,
  code: OrderCodeSchema,
  status: OrderStatusSchema,
  column: StoreQueueColumnSchema,
  channel: OrderChannelSchema,
  mode: FulfillmentModeSchema,
  version: OptimisticVersionSchema,
  createdAt: InstantSchema,
  /** Ha quanto tempo o pedido espera. E a primeira pergunta do balconista. */
  waitingSeconds: z.number().int().nonnegative(),
  /** Negativo quando o prazo ja passou — o painel destaca, e o job de SLA cancela. */
  slaRemainingSeconds: z.number().int().nullable(),
  slaLevel: StoreQueueSlaLevelSchema,
  /** `true` **so** com `payment.status = PAGO` (`CONTRACTS §2.4`). Intencao de pagar nao e pago. */
  paid: z.boolean(),
  paymentMethod: PaymentMethodSchema.nullable(),
  paymentStatus: PaymentStatusSchema.nullable(),
  itemCount: z.number().int().positive(),
  /** Quantas linhas ja foram separadas. `itemCount - pickedCount` e o que falta. */
  pickedCount: z.number().int().nonnegative(),
  total: NonNegativeMoneySchema,
  /** Ha ajuste esperando o cliente (`AJUSTE_PENDENTE`)? O painel nao oferece acao nesse estado. */
  adjustmentPending: z.boolean(),
});
export type StoreQueueItem = z.infer<typeof StoreQueueItemSchema>;

/**
 * `seq` e o **carimbo de leitura**: o maior `storeSeq` que esta filial ja alocou no instante
 * da consulta. O cliente o guarda e o devolve em `?sinceSeq=` na proxima volta do polling.
 *
 * `number` e nao `string`: `store.panelSeq` e `BigInt` no banco porque coluna de sequencia
 * nasce `BigInt`, mas `Number.MAX_SAFE_INTEGER` sao 9e15 eventos **em uma filial** — a
 * instancia inteira nao chega la em mil anos. Serializar como string custaria conversao em
 * toda a cadeia para cobrir um caso inalcancavel.
 */
export const StoreQueueResponseSchema = z.object({
  data: z.array(StoreQueueItemSchema),
  /** Devolva em `?sinceSeq=` na proxima volta. */
  seq: z.number().int().nonnegative(),
  /** `true` quando `sinceSeq` foi aplicado — a resposta e delta, e nao a fila inteira. */
  delta: z.boolean(),
  /**
   * Ids que **sairam** da fila desde `sinceSeq` (chegaram a terminal). Sem eles, o delta
   * so sabe acrescentar, e um pedido cancelado por outro balconista ficaria na tela para
   * sempre. Vazio quando `delta` e falso.
   */
  removed: z.array(Uuidv7Schema),
});
export type StoreQueueResponse = z.infer<typeof StoreQueueResponseSchema>;

// ---------------------------------------------------------------------------
// O detalhe que o painel abre
// ---------------------------------------------------------------------------

/**
 * A linha do pedido **como o balconista a separa**.
 *
 * `onHandQty` esta aqui e **nao** existe em nenhuma resposta do cliente. Nao e descuido dos
 * dois lados: `CONTRACTS §2.13` da ao cliente uma **faixa** (`AvailabilityBand`), e a matriz
 * de `SECURITY §4.2` da ao balconista `Inventory: R store`. Ele separa — precisa do numero.
 */
export const StoreOrderItemSchema = z.object({
  id: Uuidv7Schema,
  productId: Uuidv7Schema,
  displayName: z.string().min(1),
  presentation: z.string().nullable(),
  erpSku: z.string().min(1),
  gtin: z.string().nullable(),
  quantity: z.number().int().positive(),
  pickedQuantity: z.number().int().nonnegative(),
  /** `PENDENTE` · `SEPARADO` · `FALTA` · `SUBSTITUIDO` (`OrderItemStatus` do DATA_MODEL). */
  status: z.string().min(1),
  unitPrice: NonNegativeMoneySchema,
  lineTotal: NonNegativeMoneySchema,
  requiresPrescription: z.boolean(),
  /** Saldo absoluto na filial. `null` quando a instancia nao acompanha estoque (`UNTRACKED`). */
  onHandQty: z.number().int().nullable(),
});
export type StoreOrderItem = z.infer<typeof StoreOrderItemSchema>;

/** O contato do pedido — `R minimo (nome, telefone do pedido)` da matriz de `SECURITY §4.2`. */
export const StoreOrderContactSchema = z.object({
  name: z.string().min(1),
  phone: z.string().min(1),
});
export type StoreOrderContact = z.infer<typeof StoreOrderContactSchema>;

export const StoreOrderAddressSchema = z.object({
  zoneName: z.string().nullable(),
  street: z.string().min(1),
  number: z.string().nullable(),
  complement: z.string().nullable(),
  /** R-28 — e o campo que o motoboy usa de verdade no interior. */
  reference: z.string().nullable(),
  city: z.string().min(1),
  uf: z.string().length(2),
});
export type StoreOrderAddress = z.infer<typeof StoreOrderAddressSchema>;

/** O ajuste aberto, quando ha um. E o que a tela mostra enquanto espera o cliente. */
export const StoreOrderAdjustmentSchema = z.object({
  id: Uuidv7Schema,
  status: z.enum(['PENDENTE', 'APROVADO', 'RECUSADO', 'TIMEOUT_APLICADO']),
  respondBy: InstantSchema,
  defaultOnTimeout: z.enum(['ENVIAR_PARCIAL', 'CANCELAR']),
  /**
   * Negativo = a favor do cliente. Na ruptura e sempre `<= 0`, e isso e invariante e nao
   * expectativa: `INV-O2` (`recompute(quote) <= contract.total`) e a razao — a loja causou a
   * falta, e o pedido nao encarece por causa dela.
   */
  delta: MoneySchema,
  newTotalIfPartial: NonNegativeMoneySchema,
  /** Quanto volta ao cliente se ele aceitar o parcial e o pedido ja estiver pago. */
  refundIfPartial: NonNegativeMoneySchema,
  shortages: z.array(
    z.object({
      itemId: Uuidv7Schema,
      missingQuantity: z.number().int().positive(),
      reason: z.string().min(1),
    }),
  ),
});
export type StoreOrderAdjustment = z.infer<typeof StoreOrderAdjustmentSchema>;

/** A resposta de toda transicao do Painel: o pedido como ele ficou. */
export const StoreOrderDetailSchema = z.object({
  id: Uuidv7Schema,
  code: OrderCodeSchema,
  version: OptimisticVersionSchema,
  status: OrderStatusSchema,
  storeId: Uuidv7Schema,
  channel: OrderChannelSchema,
  mode: FulfillmentModeSchema,
  createdAt: InstantSchema,
  acceptDeadlineAt: InstantSchema.nullable(),
  waitingSeconds: z.number().int().nonnegative(),
  contact: StoreOrderContactSchema,
  address: StoreOrderAddressSchema.nullable(),
  items: z.array(StoreOrderItemSchema),
  subtotal: NonNegativeMoneySchema,
  discounts: NonNegativeMoneySchema,
  deliveryFee: NonNegativeMoneySchema,
  total: NonNegativeMoneySchema,
  /** Quanto o cliente vai entregar em dinheiro. O troco e `changeFor - total` (INV-O10). */
  changeFor: NonNegativeMoneySchema.nullable(),
  paymentMethod: PaymentMethodSchema.nullable(),
  paymentStatus: PaymentStatusSchema.nullable(),
  paid: z.boolean(),
  notesToStore: z.string().nullable(),
  requiresPrescription: z.boolean(),
  /**
   * Ha MEDICAMENTO no pedido — a condicao exata do CRF, e nao `requiresPrescription`.
   *
   * ## O defeito que este campo fecha
   *
   * `API §6.19` e `DOMAIN` INV-O4 dizem a mesma coisa: *"`ready` exige
   * `dispensedByPharmacistCrf` **quando ha medicamento** (REG-005)"*. O servidor aplica isso
   * literalmente — `mark-order-ready.usecase.ts` calcula
   * `items.some((i) => isMedication(i.regulatoryClassSnap))`.
   *
   * O Painel nao tinha como aplicar a MESMA regra: nenhum campo desta resposta carrega classe
   * regulatoria, entao a tela usava o unico booleano parecido que existia,
   * `requiresPrescription`. Os dois divergem exatamente onde mais dói — em **MIP**, o
   * medicamento isento de prescricao, que e o item mais vendido de uma farmacia. Para uma
   * dipirona: `requiresPrescription = false`, `isMedication = true`.
   *
   * Consequencia medida em navegador: a tela nao mostrava o campo de CRF, o operador nao tinha
   * onde digita-lo, e `Marcar como pronto` respondia *"Pedido com medicamento exige
   * dispensedByPharmacistCrf antes de PRONTO"* — para sempre. **O pedido mais comum de uma
   * farmacia ficava preso em `EM_SEPARACAO`, sem saida pela tela.**
   *
   * O campo e derivado, nao armazenado: a projecao o calcula com a mesma funcao do caso de uso,
   * `isMedication` do `@farmacia/domain`. Duas leituras da mesma regra, uma fonte so.
   */
  hasMedication: z.boolean(),
  dispensedByPharmacistCrf: z.string().nullable(),
  adjustment: StoreOrderAdjustmentSchema.nullable(),
  /** Os eventos que o painel pode oferecer a partir deste estado (`allowedEventsFrom`). */
  allowedEvents: z.array(z.string().min(1)),
});
export type StoreOrderDetail = z.infer<typeof StoreOrderDetailSchema>;

// ---------------------------------------------------------------------------
// Aceite e recusa
// ---------------------------------------------------------------------------

/**
 * O aceite nao tem corpo. `.strict()` sobre `{}` e deliberado: um corpo aberto convida a
 * mandar `totalCents` "so para conferir", e `SECURITY §8.1` e literal — o preco nunca sobe
 * pelo corpo de uma requisicao.
 */
export const AcceptOrderRequestSchema = z.object({}).strict();
export type AcceptOrderRequest = z.infer<typeof AcceptOrderRequestSchema>;

/**
 * **Lista fechada** — `ADR-0013 §2` P4: *"aceite/recusa com motivo em lista fechada"*, e
 * *"motivo vira metrica, nao texto livre"*. Os cinco valores sao os de `API §6.19`.
 */
export const STORE_REJECT_REASON_VALUES = [
  'SEM_ESTOQUE',
  'FORA_DE_AREA',
  'FECHADO',
  'SUSPEITA_FRAUDE',
  'OUTRO',
] as const;
export const StoreRejectReasonSchema = z.enum(STORE_REJECT_REASON_VALUES);
export type StoreRejectReason = z.infer<typeof StoreRejectReasonSchema>;

/**
 * A recusa **exige motivo**, e o `note` deixa de ser opcional quando o motivo e `OUTRO`.
 *
 * Sem essa segunda regra, `OUTRO` viraria o valor que absorve tudo e a metrica de P4 morreria
 * no primeiro mes — que e exatamente o defeito que a lista fechada existe para impedir.
 */
export const RejectOrderRequestSchema = z
  .object({
    reason: StoreRejectReasonSchema,
    note: z.string().min(1).max(280).optional(),
  })
  .strict()
  .refine((body) => body.reason !== 'OUTRO' || (body.note ?? '').trim().length > 0, {
    message: 'Motivo OUTRO exige nota — senão a métrica de recusa (ADR-0013 P4) perde o sentido.',
    path: ['note'],
  });
export type RejectOrderRequest = z.infer<typeof RejectOrderRequestSchema>;

// ---------------------------------------------------------------------------
// Separacao item a item
// ---------------------------------------------------------------------------

/**
 * `pick` marca a linha separada. `quantity` ausente = a linha inteira, que e o caso comum.
 *
 * INV-O7 — `quantityPicked <= quantityOrdered`: quantidade maior e `422 VALIDATION_ERROR`, e
 * nao um `min()` silencioso.
 */
export const PickOrderItemRequestSchema = z
  .object({
    quantity: z.number().int().positive().optional(),
  })
  .strict();
export type PickOrderItemRequest = z.infer<typeof PickOrderItemRequestSchema>;

// ---------------------------------------------------------------------------
// Ruptura
// ---------------------------------------------------------------------------

/** `API §6.20.11` — os quatro motivos de ruptura, lista fechada. */
export const SHORTAGE_REASON_VALUES = [
  'SEM_ESTOQUE',
  'AVARIADO',
  'VENCIDO',
  'DIVERGENCIA_CADASTRO',
] as const;
export const ShortageReasonSchema = z.enum(SHORTAGE_REASON_VALUES);
export type ShortageReason = z.infer<typeof ShortageReasonSchema>;

/**
 * `POST …/items/{itemId}/shortage` — `API §6.20.11`, literal.
 *
 * `suggestedSubstituteProductId` e **sugestao registrada**, nunca aplicacao: `ADR-0013 §2` P5
 * — *"substituicao de medicamento exige criterio farmaceutico — sugestao, nunca automatica"*.
 * A confirmacao e a rota `…/substitute`, que e **prevista** e nao existe no MVP (`API §6.19`).
 */
export const ShortageRequestSchema = z
  .object({
    missingQuantity: z.number().int().positive(),
    reason: ShortageReasonSchema,
    suggestedSubstituteProductId: Uuidv7Schema.optional(),
  })
  .strict();
export type ShortageRequest = z.infer<typeof ShortageRequestSchema>;

/** `API §6.20.11` — a resposta da ruptura. */
export const ShortageResponseSchema = z.object({
  order: StoreOrderDetailSchema,
  adjustmentId: Uuidv7Schema,
  /** O cliente tem `counter.shortage.timeoutMinutes` para responder (default 10). */
  customerRespondBy: InstantSchema,
  /** O que acontece se ele nao responder — `counter.shortage.onTimeout` daquele momento. */
  defaultOnTimeout: z.enum(['ENVIAR_PARCIAL', 'CANCELAR']),
  /** Recalculado pelo motor de preco sobre a cesta que sobrou. Nunca subtraido a mao. */
  newTotalIfPartial: NonNegativeMoneySchema,
  /** `> 0` so quando o pedido ja esta pago: e o que ha de estornar. */
  refundIfPartial: NonNegativeMoneySchema,
});
export type ShortageResponse = z.infer<typeof ShortageResponseSchema>;

// ---------------------------------------------------------------------------
// Pronto, despacho e entrega
// ---------------------------------------------------------------------------

/**
 * `ready` — `API §6.19`: *"exige `dispensedByPharmacistCrf` quando ha medicamento (REG-005)"*.
 *
 * O campo e opcional **no schema** e obrigatorio **no efeito**: sem medicamento no pedido a
 * guarda `PHARMACIST_CRF_WHEN_MEDICATION` de `@farmacia/domain` nem se aplica, e exigi-lo no
 * Zod obrigaria a farmacia que so vende sabonete a digitar um CRF. Com medicamento, a maquina
 * de estados recusa a transicao — e a recusa vem do dominio, nao de um `if` da borda.
 *
 * DUVIDA (reportada): **nenhum documento soberano diz de onde vem o CRF nesta requisicao**.
 * `API §6.19` cita a exigencia e nao publica o schema. O adotado: ausente, vale o CRF **do
 * proprio ator** quando ele e `PHARMACIST` com CRF ativo; informado, ele tem de pertencer a um
 * farmaceutico de CRF ativo **vinculado aquela filial** — senao `403 PHARMACIST_CRF_INACTIVE`.
 * Aceitar a string crua transformaria REG-005 em campo de texto.
 */
export const MarkReadyRequestSchema = z
  .object({
    dispensedByPharmacistCrf: CrfSchema.optional(),
  })
  .strict();
export type MarkReadyRequest = z.infer<typeof MarkReadyRequestSchema>;

/** `ready` devolve o pedido e, em `PICKUP`, o codigo de retirada — **uma unica vez**. */
export const MarkReadyResponseSchema = z.object({
  order: StoreOrderDetailSchema,
  /**
   * Codigo de retirada em claro. O banco guarda **so o hash** (`order.pickupCodeHash`), entao
   * esta e a unica resposta do sistema que o exibe ao balcao; o cliente o recebe pela
   * notificacao de `orders.Order.Ready` e por `GET /v1/me/orders/{id}/pickup-code`.
   */
  pickupCode: z.string().min(4).max(12).nullable(),
  pickupExpiresAt: InstantSchema.nullable(),
});
export type MarkReadyResponse = z.infer<typeof MarkReadyResponseSchema>;

/**
 * `dispatch` — `API §6.19`: *"bloqueia se `COURIER_TRAINING_EXPIRED` (REG-004)"*.
 *
 * DUVIDA (reportada): a tabela `courier` e **prevista** (`CONTRACTS §8.1`), entao no MVP nao
 * existe fonte independente para a validade do treinamento. O que existe e a coluna de
 * snapshot `delivery.courierTrainingValidUntilSnap` — e e ela que este corpo alimenta: a
 * validade e **declarada pelo balcao e registrada com quem despachou**, que e o que a torna
 * auditavel enquanto a tabela nao chega. Ausente ou vencida ⇒ a guarda do dominio barra
 * (fail-closed, RN-096). Isto e registro, nao verificacao — e a distincao esta escrita aqui de
 * proposito.
 */
export const DispatchOrderRequestSchema = z
  .object({
    courierName: z.string().min(2).max(120),
    /** `YYYY-MM-DD`. Vencida em relacao ao relogio do servidor ⇒ `422 COURIER_TRAINING_EXPIRED`. */
    courierTrainingValidUntil: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    courierId: Uuidv7Schema.optional(),
  })
  .strict();
export type DispatchOrderRequest = z.infer<typeof DispatchOrderRequestSchema>;

export const DispatchOrderResponseSchema = z.object({
  order: StoreOrderDetailSchema,
  /**
   * Os 4 digitos que o cliente confere na porta (RN-097). Como o codigo de retirada, o banco
   * guarda so o hash (`delivery.confirmationCodeHash`).
   *
   * DUVIDA (reportada): **nenhum evento do MVP leva este codigo ao cliente** —
   * `orders.Order.Dispatched` esta na lista de previstos de `CONTRACTS §4.2`. Enquanto ele nao
   * existir, o unico lugar em que o codigo aparece e esta resposta, e quem o transmite e o
   * balconista. Nao inventei evento para fechar o buraco.
   */
  deliveryCode: z.string().length(4),
});
export type DispatchOrderResponse = z.infer<typeof DispatchOrderResponseSchema>;

/**
 * `deliver` fecha **as duas modalidades** — e e por isso que o corpo tem so `code`.
 *
 * `CONTRACTS §2.1`: no MVP a retirada concluida e `ENTREGUE` com `fulfillmentMode = PICKUP`;
 * `RETIRADO` existe no enum e a aplicacao nao o produz. Entao:
 *
 * | Modalidade | Transicao | Codigo conferido |
 * | --- | --- | --- |
 * | `DELIVERY` | `EM_ROTA --ConfirmDelivery--> ENTREGUE` | 4 digitos de `delivery.confirmationCodeHash` |
 * | `PICKUP` | `PRONTO --ConfirmPickup--> ENTREGUE` | 6 caracteres de `order.pickupCodeHash` |
 */
export const DeliverOrderRequestSchema = z
  .object({
    code: z.string().min(4).max(12),
  })
  .strict();
export type DeliverOrderRequest = z.infer<typeof DeliverOrderRequestSchema>;

// ---------------------------------------------------------------------------
// O ticket
// ---------------------------------------------------------------------------

/** Impressora termica de 80 mm em fonte A: **48 colunas** (`ADR-0013 §2`, P3). */
export const THERMAL_TICKET_COLUMNS = 48;

/**
 * `print` devolve o **conteudo**, nao um PDF.
 *
 * `ADR-0013` (revisao ADR-0019 §3) e explicito: *"o ticket e tela imprimivel
 * (`window.print()` + folha 80 mm)"*, e a chave `counter.printing` foi **removida** do
 * catalogo de perfil (D-13) — nao ha instancia sem impressao. Gerar PDF no servidor
 * acrescentaria uma dependencia binaria, um caminho de fonte e um arquivo temporario para
 * produzir o que o navegador ja imprime.
 *
 * `lines` e o mesmo texto de `text`, quebrado nas 48 colunas: a tela imprime `lines` com
 * `white-space: pre`, e um agente ESC/POS futuro (previsto em P3) consome `text` sem
 * reformatar.
 */
export const StoreTicketSchema = z.object({
  orderId: Uuidv7Schema,
  code: OrderCodeSchema,
  printedAt: InstantSchema,
  columns: z.literal(THERMAL_TICKET_COLUMNS),
  lines: z.array(z.string()),
  text: z.string().min(1),
});
export type StoreTicket = z.infer<typeof StoreTicketSchema>;
